import { buildDeliveryAvailability, validateStockStatus } from '@libs/analytics';
import { formatAutoshipFrequency, getENV, useDecisions } from '@libs/common-next';
import { logger } from '@libs/core';

import { useProduct } from '@/entities/product';
import type { AddToSubscriptionCrawlEvent, AddToSubscriptionPrecrawlEvent, Currency } from '@/shared/analytics';
import { dataLayerPush } from '@/shared/analytics';

type SendAnalyticsParams = {
    quantity: number;
    public_id: string;
    isOneTimeOrder: boolean;
    unitOfMeasure: number | null;
    frequencyAmount: number | null;
};

export function useAutoshipAnalyticsEvent() {
    const { product, tailored } = useProduct();
    const autoshipFlag = useDecisions('ff_autoship');

    const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';
    const currency: Currency = isCA ? 'CAD' : 'USD';

    const send = ({ quantity, public_id, unitOfMeasure, frequencyAmount, isOneTimeOrder }: SendAnalyticsParams) => {
        if (!product || !tailored) {
            logger.error(
                '[ADD TO SUBSCRIPTION ANALYTICS][Error]: Failed to send analytics event - missing required data',
                {
                    hasProduct: Boolean(product),
                    hasTailoredData: Boolean(tailored),
                    subscriptionId: public_id,
                }
            );
            return;
        }

        const { merchandisingHierarchy, sku, brand, price: productPrice, deliveryMethods } = product;

        const price = productPrice.sale ?? productPrice.original ?? 0.0;
        const subscriptionFrequency = formatAutoshipFrequency(frequencyAmount, unitOfMeasure) ?? '';

        const baseEventData = {
            autoship_autoselected: 'no' as const,
            currency,
            event: 'add_to_subscription' as const,
            product_brand: brand?.name ?? '',
            product_category: merchandisingHierarchy?.vicePresidentName ?? '',
            product_category2: merchandisingHierarchy?.division ?? '',
            product_category3: merchandisingHierarchy?.department ?? '',
            product_category4: merchandisingHierarchy?.class ?? '',
            product_category5: merchandisingHierarchy?.category ?? '',
            product_id: sku,
            product_price: price,
            product_quantity: quantity,
            subscription_frequency: isOneTimeOrder ? 'One-time' : subscriptionFrequency,
            subscription_id: public_id,
            subscription_type: 'add to existing',
            value: price * quantity,
        };

        const isCustomPharmacyOOS = Boolean(product?.pharmacy?.oos);

        if (autoshipFlag === 'precrawl') {
            const availableStock = tailored?.variants[sku]?.['STH']?.availableStock;
            const stockStatusText = validateStockStatus({
                availableStock: availableStock ?? undefined,
                isCustomPharmacyOOS,
                minimumCaseQty: product.deliveryMethods.STH?.purchaseQuantities?.min ?? 1,
            });

            const precrawlEvent: AddToSubscriptionPrecrawlEvent = {
                ...baseEventData,
                autoship_stock_availability: stockStatusText,
            };

            dataLayerPush(precrawlEvent);

            return;
        }

        const deliveryMethodData = buildDeliveryAvailability({
            deliveryMethods,
            tailoredVariant: tailored.variants[sku],
            purchaseOption: 'autoship',
            isCustomPharmacyOOS,
        });

        const crawlEvent: AddToSubscriptionCrawlEvent = {
            ...baseEventData,
            fulfillment_method_order: 'sth autoship',
            ...deliveryMethodData,
        };

        dataLayerPush(crawlEvent);
    };

    return { send };
}
